import {Spinner} from '@/components/ui/Spinner';
import {formatNumber} from '@tryghost/shade/utils';
import {useMediaQuery} from '@/hooks/useMediaQuery';
import {usePricing} from '@/hooks/useBilling';
import {useState} from 'react';
import type {BillingPeriod, PricingPlan} from '@/types/api';

function formatPrice(dollars: number): string {
    if (dollars === 0) {
        return 'Free';
    }
    return `$${formatNumber(Math.round(dollars))}`;
}

function PeriodToggle({
    selected,
    onChange,
    hasLifetime
}: {
    selected: BillingPeriod;
    onChange: (period: BillingPeriod) => void;
    hasLifetime: boolean;
}) {
    const options: { value: BillingPeriod; label: string; badge?: string }[] = [
        {value: 'monthly', label: 'Monthly'},
        {value: 'yearly', label: 'Yearly', badge: 'Save ~17%'}
    ];
    if (hasLifetime) {
        options.push({value: 'lifetime', label: 'Lifetime'});
    }

    return (
        <div
            style={{
                display: 'inline-flex',
                background: 'var(--tt-card-bg)',
                border: '1px solid var(--tt-card-border)',
                borderRadius: 8,
                padding: 4,
                gap: 2
            }}
        >
            {options.map(opt => (
                <button key={opt.value}
                    style={{
                        height: 40,
                        padding: '0 20px',
                        borderRadius: 6,
                        border: 'none',
                        background: selected === opt.value ? 'var(--tt-accent)' : 'transparent',
                        color: selected === opt.value ? '#fff' : 'var(--tt-text-secondary)',
                        fontSize: '1.4rem',
                        fontWeight: selected === opt.value ? 600 : 500,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        transition: 'all 0.15s ease',
                        position: 'relative',
                        whiteSpace: 'nowrap'
                    }}
                    type="button"
                    onClick={() => onChange(opt.value)}
                    onMouseEnter={(e) => {
                        if (selected !== opt.value) {
                            e.currentTarget.style.background = 'var(--tt-nav-hover-bg)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (selected !== opt.value) {
                            e.currentTarget.style.background = 'transparent';
                        }
                    }}
                >
                    {opt.label}
                    {opt.badge && (
                        <span
                            style={{
                                marginLeft: 6,
                                fontSize: '1.1rem',
                                color: selected === opt.value ? 'rgba(255,255,255,0.8)' : '#30CF43',
                                fontWeight: 600
                            }}
                        >
                            {opt.badge}
                        </span>
                    )}
                </button>
            ))}
        </div>
    );
}

function PlanCard({
    plan,
    period,
    onChoose
}: {
    plan: PricingPlan;
    period: BillingPeriod;
    onChoose: () => void;
}) {
    const isFree = plan.slug === 'free';
    // Lifetime-only pricing stays visible across billing-period tabs.
    const isLifetimeOnly = plan.pricing.lifetime && !plan.pricing.monthly && !plan.pricing.yearly;
    const effectivePeriod = isLifetimeOnly ? 'lifetime' : period;
    const pricing = plan.pricing[effectivePeriod];
    const price = pricing?.amount || 0;

    const isAvailable = isFree || (isLifetimeOnly
        ? period === 'lifetime'
        : (price > 0 || (price === 0 && period === 'monthly')));

    return (
        <div
            style={{
                background: 'var(--tt-card-bg)',
                border: '1px solid var(--tt-card-border)',
                borderRadius: 12,
                padding: 0,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                transition: 'border-color 0.2s ease',
                opacity: isAvailable ? 1 : 0.6
            }}
        >
            {/* Badge */}
            {plan.badge && (
                <div
                    style={{
                        position: 'absolute',
                        top: 20,
                        right: 20,
                        background: 'rgba(239, 90, 43, 0.1)',
                        color: 'var(--tt-accent)',
                        padding: '4px 12px',
                        borderRadius: 6,
                        fontSize: '1.15rem',
                        fontWeight: 600,
                        letterSpacing: '0.3px'
                    }}
                >
                    {plan.badge}
                </div>
            )}

            {/* Header */}
            <div style={{padding: '32px 32px 24px'}}>
                <h3
                    style={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        color: 'var(--tt-text-primary)',
                        margin: '0 0 8px 0',
                        letterSpacing: '-0.01em'
                    }}
                >
                    {plan.name}
                </h3>
                {plan.description && (
                    <p
                        style={{
                            fontSize: '1.35rem',
                            color: 'var(--tt-text-secondary)',
                            margin: 0,
                            lineHeight: 1.5
                        }}
                    >
                        {plan.description}
                    </p>
                )}
            </div>

            {/* Pricing */}
            <div style={{padding: '0 32px 24px'}}>
                {isAvailable ? (
                    <>
                        <div style={{display: 'flex', alignItems: 'baseline', gap: 6}}>
                            <span
                                style={{
                                    fontSize: '4.8rem',
                                    fontWeight: 800,
                                    color: 'var(--tt-text-primary)',
                                    lineHeight: 1,
                                    letterSpacing: '-0.02em'
                                }}
                            >
                                {formatPrice(price)}
                            </span>
                            <span
                                style={{
                                    fontSize: '1.5rem',
                                    color: 'var(--tt-text-tertiary)',
                                    fontWeight: 500
                                }}
                            >
                                {!isFree && effectivePeriod === 'monthly' && '/ mo'}
                                {!isFree && effectivePeriod === 'yearly' && '/ yr'}
                                {effectivePeriod === 'lifetime' && ''}
                            </span>
                        </div>
                        <div
                            style={{
                                fontSize: '1.3rem',
                                color: 'var(--tt-text-tertiary)',
                                marginTop: 6
                            }}
                        >
                            {isFree && 'No payment required'}
                            {!isFree && effectivePeriod === 'yearly' && 'Billed annually'}
                            {!isFree && effectivePeriod === 'monthly' && 'Billed monthly'}
                            {!isFree && effectivePeriod === 'lifetime' && 'One-time payment'}
                        </div>
                    </>
                ) : (
                    <div
                        style={{
                            fontSize: '1.5rem',
                            color: 'var(--tt-text-tertiary)',
                            fontWeight: 600
                        }}
                    >
                        Not available for this billing period
                    </div>
                )}
            </div>

            {/* CTA */}
            <div style={{padding: '0 32px 24px'}}>
                <button disabled={!isAvailable}
                    style={{
                        width: '100%',
                        height: 48,
                        borderRadius: 6,
                        border: 'none',
                        background: isAvailable ? 'var(--tt-accent)' : 'var(--tt-card-border)',
                        color: '#fff',
                        fontSize: '1.5rem',
                        fontWeight: 600,
                        cursor: isAvailable ? 'pointer' : 'not-allowed',
                        fontFamily: 'inherit',
                        transition: 'all 0.2s ease'
                    }}
                    type="button"
                    onClick={onChoose}
                    onMouseEnter={(e) => {
                        if (isAvailable) {
                            e.currentTarget.style.background = '#d94f24';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (isAvailable) {
                            e.currentTarget.style.background = 'var(--tt-accent)';
                        }
                    }}
                >
                    {isAvailable ? 'Get started' : 'Not Available'}
                </button>
            </div>

            {/* Divider */}
            <div style={{height: 1, background: 'var(--tt-divider)', margin: '0 32px'}} />

            {/* Features */}
            <div style={{padding: '24px 32px 32px', flex: 1}}>
                <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                    {plan.limits && (
                        <>
                            {plan.limits.staff !== undefined && (
                                <li style={featureStyle}>
                                    <CheckIcon />
                                    <span>
                                        <strong>{plan.limits.staff === 'Unlimited' || plan.limits.staff === -1 ? 'Unlimited' : typeof plan.limits.staff === 'number' ? formatNumber(plan.limits.staff) : plan.limits.staff}</strong> staff {plan.limits.staff === 1 ? 'user' : 'users'}
                                    </span>
                                </li>
                            )}
                            {plan.limits.members !== undefined && (
                                <li style={featureStyle}>
                                    <CheckIcon />
                                    <span>
                                        <strong>{plan.limits.members === 'Unlimited' || plan.limits.members === -1 ? 'Unlimited' : typeof plan.limits.members === 'number' ? formatNumber(plan.limits.members) : plan.limits.members}</strong> members
                                    </span>
                                </li>
                            )}
                            {plan.limits.newsletters !== undefined && (
                                <li style={featureStyle}>
                                    <CheckIcon />
                                    <span>
                                        <strong>{plan.limits.newsletters === 'Unlimited' || plan.limits.newsletters === -1 ? 'Unlimited' : typeof plan.limits.newsletters === 'number' ? formatNumber(plan.limits.newsletters) : plan.limits.newsletters}</strong> {plan.limits.newsletters === 1 ? 'newsletter' : 'newsletters'}
                                    </span>
                                </li>
                            )}
                            {plan.limits.storage_mb !== undefined && (
                                <li style={featureStyle}>
                                    <CheckIcon />
                                    <span>
                                        <strong>{plan.limits.storage_mb === 'Unlimited' || plan.limits.storage_mb === -1 ? 'Unlimited' : typeof plan.limits.storage_mb === 'number' ? formatNumber(Math.round(plan.limits.storage_mb / 1000)) + ' GB' : plan.limits.storage_mb}</strong> storage
                                    </span>
                                </li>
                            )}
                            {plan.limits.email_sends_monthly !== undefined && (
                                <li style={featureStyle}>
                                    <CheckIcon />
                                    <span>
                                        <strong>{plan.limits.email_sends_monthly === 'Unlimited' || plan.limits.email_sends_monthly === -1 ? 'Unlimited' : typeof plan.limits.email_sends_monthly === 'number' ? formatNumber(plan.limits.email_sends_monthly) : plan.limits.email_sends_monthly}</strong> emails/mo
                                    </span>
                                </li>
                            )}
                        </>
                    )}

                    {plan.features && plan.features.length > 0 && (
                        <>
                            <li style={{height: 8}} />
                            {ALL_FEATURES.map((feature) => {
                                const isIncluded = plan.features && plan.features.includes(feature.key);
                                return (
                                    <li
                                        key={feature.key}
                                        style={{
                                            ...featureStyle,
                                            opacity: isIncluded ? 1 : 0.4
                                        }}
                                    >
                                        {isIncluded ? <CheckIcon /> : <CrossIcon />}
                                        <span>{feature.label}</span>
                                    </li>
                                );
                            })}
                        </>
                    )}
                </ul>
            </div>
        </div>
    );
}

const featureStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '6px 0',
    fontSize: '1.4rem',
    color: 'var(--tt-text-primary)',
    lineHeight: 1.5
};

function CheckIcon() {
    return (
        <svg fill="none" height="18" style={{flexShrink: 0, marginTop: 2}} viewBox="0 0 24 24" width="18">
            <path
                d="M20 6L9 17l-5-5"
                stroke="#30CF43"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3"
            />
        </svg>
    );
}

function CrossIcon() {
    return (
        <svg fill="none" height="18" style={{flexShrink: 0, marginTop: 2}} viewBox="0 0 24 24" width="18">
            <path
                d="M18 6L6 18M6 6l12 12"
                stroke="var(--tt-text-tertiary)"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3"
            />
        </svg>
    );
}

const ALL_FEATURES = [
    {key: 'custom_domain', label: 'Custom domain'},
    {key: 'remove_branding', label: 'Remove branding'},
    {key: 'advanced_analytics', label: 'Advanced analytics'},
    {key: 'white_label_emails', label: 'White-label emails'},
    {key: 'api_access', label: 'API access'},
    {key: 'import_export', label: 'Import/Export'},
    {key: 'audit_logs', label: 'Audit logs'}
];

export function PricingPage() {
    const {isMobile, isTablet} = useMediaQuery();
    const isCompact = isMobile || isTablet;
    const [period, setPeriod] = useState<BillingPeriod>('monthly');

    const {data: pricingData, isLoading} = usePricing();

    const plans = pricingData?.plans || [];
    const hasLifetime = plans.some(p => (p.pricing.lifetime?.amount ?? 0) > 0);

    const handleChoosePlan = (plan: PricingPlan) => {
        if (plan.slug === 'free') {
            window.location.href = '/login';
            return;
        }

        const pricing = plan.pricing[period];
        if (!pricing) {
            return;
        }

        // Free plan - redirect to login/signup
        if (pricing.amount === 0) {
            window.location.href = '/login';
            return;
        }

        // Paid plan - redirect to Paykickstart checkout
        if (pricing.checkout_url) {
            window.location.href = pricing.checkout_url;
        } else {
            alert('Checkout is not configured for this plan. Please contact support.');
        }
    };

    if (isLoading) {
        return (
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--tt-bg)'}}>
                <Spinner />
            </div>
        );
    }

    return (
        <div style={{minHeight: '100vh', background: 'var(--tt-bg)', padding: isCompact ? '60px 20px 80px' : '100px 40px 120px'}}>
            {/* Header */}
            <div style={{textAlign: 'center', maxWidth: 700, margin: '0 auto 64px'}}>
                <h1
                    style={{
                        fontSize: isCompact ? '3.2rem' : '4.8rem',
                        fontWeight: 800,
                        color: 'var(--tt-text-primary)',
                        margin: '0 0 16px 0',
                        letterSpacing: '-0.03em',
                        lineHeight: 1.1
                    }}
                >
                    Choose your plan
                </h1>
                <p
                    style={{
                        fontSize: isCompact ? '1.6rem' : '1.8rem',
                        color: 'var(--tt-text-secondary)',
                        margin: '0 0 48px 0',
                        lineHeight: 1.6,
                        fontWeight: 400
                    }}
                >
                    Start your journey with TribeTale. All plans include unlimited posts and pages.
                </p>

                {/* Period Toggle */}
                <PeriodToggle hasLifetime={hasLifetime} selected={period} onChange={setPeriod} />
            </div>

            {/* Plans Grid */}
            <div
                style={{
                    maxWidth: 1400,
                    margin: '0 auto',
                    display: 'grid',
                    gridTemplateColumns: isCompact ? '1fr' : 'repeat(auto-fit, minmax(280px, 320px))',
                    gap: isCompact ? 20 : 24,
                    justifyContent: 'center'
                }}
            >
                {plans.map(plan => (
                    <PlanCard
                        key={plan.id}
                        period={period}
                        plan={plan}
                        onChoose={() => handleChoosePlan(plan)}
                    />
                ))}
            </div>

            {/* Footer Note */}
            <div style={{textAlign: 'center', marginTop: 80, maxWidth: 600, margin: '80px auto 0'}}>
                <p
                    style={{
                        fontSize: '1.4rem',
                        color: 'var(--tt-text-tertiary)',
                        lineHeight: 1.6
                    }}
                >
                    Need something custom?{' '}
                    <a
                        href="mailto:support@tribetale.com"
                        style={{
                            color: 'var(--tt-accent)',
                            textDecoration: 'none',
                            fontWeight: 600
                        }}
                    >
                        Contact us
                    </a>
                </p>
            </div>
        </div>
    );
}
